import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaw,
  faMapMarkerAlt,
  faPhoneAlt,
  faTimesCircle,
  faClock,
  faUser,
  faCheckCircle,
  faDollarSign,
  faHistory,
  faInfoCircle,
  faStar,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { CURRENCY_CODE, WALK_STATUS } from "../../../config/constants";
import StarRating from "../../../components/StarRating";
import { capitalizeWords, routes } from "../../../config/utility";
import { Link } from "react-router-dom";
import TimeAnalytics from "../../../components/TimeAnalytics";

const WalkDetailModal = ({ walk, isOpen, onClose }) => {
  if (!walk) return null;

  const {
    id,
    status,
    duration,
    clean_pet,
    emergency_no,
    cancel_reason,
    scheduled_at,
    additional_info,
    charges,
    walker,
    user,
    pickup_location,
    pets,
    tools,
    created_at,
    status_time,
  } = walk;

  const statusStyles = {
    requested: "bg-yellow-100 text-yellow-800",
    walker_assigned: "bg-blue-100 text-blue-800",
    in_progress: "bg-blue-500 text-white",
    completed: "bg-green-100 text-green-800",
    canceled: "bg-red-100 text-red-800",
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-full p-2 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg sm:max-w-7xl transform overflow-hidden rounded-lg bg-white p-2 sm:p-8 text-left align-middle shadow-xl transition-all">

                {/* Header */}
                <div className="flex justify-between items-start mb-2 sm:mb-4">
                  <Link to={routes.walk_single.replace(':walk_id?', id)}>
                    <h3 className="text-lg sm:text-2xl font-semibold text-gray-900">
                      Walk Details - ID #{id}
                    </h3>
                  </Link>

                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 transition-transform transform hover:scale-110"
                  >
                    <FontAwesomeIcon icon={faTimesCircle} className="w-5 h-5" />
                  </button>
                </div>

                {/* Status Badge */}
                <div
                  className={`px-3 py-1 text-center rounded-full text-xs sm:text-sm font-semibold w-max ${statusStyles[status] || "bg-gray-100 text-gray-800"
                    }`}
                >
                  {status.replace("_", " ").toUpperCase()}
                </div>

                {/* Status Timeline */}
                {status_time && Object.keys(status_time).length > 0 && (
                  <div className="mt-4 sm:mt-6 bg-gray-50 p-2 sm:p-4 rounded-lg shadow-inner">
                    <h4 className="text-gray-700 font-semibold mb-2 sm:mb-3 flex items-center text-base sm:text-lg">
                      <FontAwesomeIcon icon={faHistory} className="mr-2 text-blue-600" />
                      Status Timeline
                    </h4>
                    <div className="border-l-2 border-gray-300 pl-2 sm:pl-4 space-y-2 sm:space-y-3">
                      {Object.entries(WALK_STATUS).map(([key, label]) =>
                        status_time[label] ? (
                          <div key={label} className="flex items-center space-x-2 sm:space-x-3">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                            <span className="font-semibold text-xs sm:text-base">{capitalizeWords(key)} </span>
                            <span className="text-gray-600 text-xs sm:text-sm">
                              {new Date(status_time[label]).toLocaleString()}
                            </span>
                          </div>
                        ) : null
                      )}
                    </div>
                  </div>
                )}

                {/* Schedule Information */}
                <div className="mt-4 sm:mt-6 bg-gradient-to-r from-green-50 to-blue-50 p-4 sm:p-6 rounded-lg border border-green-200">
                  <h4 className="text-gray-700 font-semibold mb-3 sm:mb-4 flex items-center text-base sm:text-lg">
                    <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-green-600" />
                    Schedule Information
                  </h4>
                  <div className="flex items-center space-x-4">
                    {scheduled_at ? (
                      <>
                        <div className="flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-lg border border-green-300">
                          <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-green-600" />
                          <span className="font-semibold">Scheduled Walk</span>
                        </div>
                        <div className="text-gray-700">
                          <div className="font-semibold">
                            {new Date(scheduled_at).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </div>
                          <div className="text-sm text-gray-600">
                            {new Date(scheduled_at).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            })}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {(() => {
                              const now = new Date();
                              const scheduled = new Date(scheduled_at);
                              const diffTime = scheduled - now;
                              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                              if (diffDays < 0) {
                                return `Scheduled for ${Math.abs(diffDays)} day(s) ago`;
                              } else if (diffDays === 0) {
                                return 'Scheduled for today';
                              } else if (diffDays === 1) {
                                return 'Scheduled for tomorrow';
                              } else {
                                return `Scheduled for ${diffDays} day(s) from now`;
                              }
                            })()}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-lg border border-blue-300">
                        <FontAwesomeIcon icon={faClock} className="mr-2 text-blue-600" />
                        <span className="font-semibold">Instant Walk</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Time Analytics */}
                <TimeAnalytics status_time={status_time} WALK_STATUS={WALK_STATUS} />

                {/* Main Details */}
                <div className="mt-4 sm:mt-6 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">

                    {/* Customer Details */}
                    <div className="flex flex-col space-y-3 sm:space-y-4 rounded-lg shadow-lg bg-gray-100 p-4 sm:p-10 max-w-full sm:max-w-lg mx-auto">
                      {/* Heading */}
                      <div className="text-center border-b pb-2 sm:pb-3">
                        <h3 className="text-lg sm:text-2xl font-bold text-blue-700">
                          <FontAwesomeIcon icon={faUser} className="mr-2 text-blue-600" />
                          Customer Details
                        </h3>
                      </div>

                      {/* User Profile */}
                      <div className="flex flex-col items-center space-y-2 sm:space-y-3">
                        <img
                          src={user?.profile_photo || "https://via.placeholder.com/80"}
                          alt={user?.name || "User Profile"}
                          className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-gray-300 shadow-md"
                        />
                        <div className="text-center">
                          <span className="text-base sm:text-lg font-semibold">{user?.name || "N/A"}</span>
                          <p className="text-xs sm:text-sm text-gray-500">{user?.email || "No Email"}</p>
                        </div>
                      </div>

                      {/* User Info */}
                      <div className="space-y-2 sm:space-y-3 text-gray-700 text-xs sm:text-base">
                        {/* Phone Number */}
                        <div className="flex items-center space-x-2">
                          <FontAwesomeIcon icon={faPhoneAlt} className="text-green-600" />
                          <span>
                            <strong>Phone:</strong> {user?.phone || "N/A"}
                          </span>
                        </div>

                        {/* Join Date */}
                        <div className="flex items-center space-x-2">
                          <FontAwesomeIcon icon={faCalendarAlt} className="text-purple-600" />
                          <span>
                            <strong>Joined:</strong> {user?.join_at || "Unknown"}
                          </span>
                        </div>

                        {/* Location */}
                        {user?.pin_location && (
                          <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="text-red-600" />
                            <span>
                              <strong>Pin Location:</strong> {user.pin_location.address || "N/A"}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Walker Details */}
                    {walker && (
                      <div className="flex flex-col space-y-3 sm:space-y-4 rounded-lg shadow-lg bg-gray-300 p-4 sm:p-10 max-w-full sm:max-w-lg mx-auto">
                        {/* Heading */}
                        <div className="text-center border-b pb-2 sm:pb-3">
                          <h3 className="text-lg sm:text-2xl font-bold text-purple-700">
                            <FontAwesomeIcon icon={faUser} className="mr-2 text-purple-600" />
                            Assigned Walker
                          </h3>
                        </div>

                        {/* Walker Profile */}
                        <div className="flex flex-col items-center space-y-2 sm:space-y-3">
                          <img
                            src={walker?.profile_photo || "https://via.placeholder.com/80"}
                            alt={walker?.name || "Walker Profile"}
                            className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border-4 border-gray-300 shadow-md"
                          />
                          <div className="text-center">
                            <span className="text-base sm:text-lg font-semibold">{walker?.name || "N/A"}</span>
                            <p className="text-xs sm:text-sm text-gray-500">{walker?.email || "No Email"}</p>
                          </div>
                        </div>

                        {/* Walker Info */}
                        <div className="space-y-2 sm:space-y-3 text-gray-700 text-xs sm:text-base">
                          {/* Phone Number */}
                          <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faPhoneAlt} className="text-green-600" />
                            <span>
                              <strong>Phone:</strong> {walker?.phone || "N/A"}
                            </span>
                          </div>

                          {/* Join Date */}
                          <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faCalendarAlt} className="text-purple-600" />
                            <span>
                              <strong>Joined:</strong> {walker?.join_at || "Unknown"}
                            </span>
                          </div>

                          {/* Average Rating */}
                          <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faStar} className="text-yellow-500" />
                            <span>
                              <strong>Walk Rating:</strong>
                            </span>
                            <StarRating rating={walker?.avg_rating} />
                            <span>
                              <strong>({walker?.avg_rating})</strong>
                            </span>
                          </div>

                          {/* Location */}
                          {walker?.pin_location && (
                            <div className="flex items-center space-x-2">
                              <FontAwesomeIcon icon={faMapMarkerAlt} className="text-red-600" />
                              <span>
                                <strong>Current Location:</strong> {walker.pin_location.address || "N/A"}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                  </div>

                  {walker && (
                    <div className="flex items-center space-x-2 text-gray-700">
                      <FontAwesomeIcon icon={faUser} className="text-purple-600" />
                      <span>
                        Assigned Walker: <strong>{walker.name}</strong>
                      </span>
                    </div>
                  )}

                  <div className="flex items-center space-x-2 text-gray-700">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-red-600" />
                    <span>
                      Pickup Location: <strong>{pickup_location?.address || "N/A"}</strong>
                    </span>
                  </div>

                  <div className="flex items-center space-x-2 text-gray-700">
                    <FontAwesomeIcon icon={faClock} className="text-purple-600" />
                    <span>
                      Duration: <strong>{duration || "N/A"}</strong>
                    </span>
                  </div>

                  <div className="flex items-center space-x-2 text-gray-700">
                    <FontAwesomeIcon icon={faPhoneAlt} className="text-blue-600" />
                    <span>
                      Emergency Contact: <strong>{emergency_no || "N/A"}</strong>
                    </span>
                  </div>


                  <div className="mt-6 flex items-center space-x-2 text-gray-700">
                    <FontAwesomeIcon
                      icon={faPaw}
                      className={clean_pet ? "text-green-600" : "text-gray-400"}
                    />
                    <span>
                      {clean_pet
                        ? "Pet Cleaning Included"
                        : "Pet Cleaning Not Included"}
                    </span>
                  </div>



                  {/* Pets */}
                  {pets && pets.length > 0 && (

                    <div className="mt-6 space-y-4">
                      <h4 className="text-gray-700 font-semibold">Pets</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                        {pets.map((pet) => (
                          <div
                            key={pet.id}
                            className="bg-gray-100 p-3 rounded-lg shadow-md text-center"
                          >
                            <img
                              src={pet.primary_image_url}
                              alt={pet.name}
                              className="w-16 h-16 rounded-full mx-auto object-cover shadow-md mb-2"
                            />
                            <h5 className="text-gray-800 font-semibold">
                              {pet.name}
                            </h5>
                            <p className="text-sm text-gray-500">
                              {pet.type.name}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tools */}
                  {tools && tools.length > 0 && (
                    <div className="mt-6 space-y-4">
                      <h4 className="text-gray-700 font-semibold">Tools</h4>
                      <div className="space-y-2">
                        {tools.map((tool) => (

                          <div
                            key={tool.id}
                            className="flex justify-between items-center bg-gray-50 p-3 rounded-lg"
                          >
                            <span className="font-semibold text-gray-800">
                              {tool.name}
                            </span>
                            <span className="text-gray-600">
                              {tool.price} {CURRENCY_CODE}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Additional Information */}
                  {additional_info && (
                    <div className="mt-6 bg-gray-50 p-4 rounded-lg shadow-inner">
                      <h4 className="text-gray-700 font-semibold">
                        Additional Information
                      </h4>
                      <p className="text-gray-600">{additional_info}</p>
                    </div>
                  )}

                  {cancel_reason && (
                    <div className="text-sm text-red-600">
                      <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                      <span>Cancel Reason: {cancel_reason}</span>
                    </div>
                  )}


                </div>

                {/* Charges */}
                <div className="bg-gray-100 p-6 rounded-lg shadow-md mt-6">
                  <h2 className="text-xl font-semibold mb-4">Cost Breakdown</h2>
                  <div className="space-y-3">
                    {Object.entries(charges)
                      .filter(([key, value]) => parseFloat(value) > 0 && key !== "total")
                      .map(([key, value], index) => (
                        <div key={index} className="flex justify-between">
                          <span className="text-gray-600 capitalize">
                            <FontAwesomeIcon icon={faDollarSign} className="mr-2" />
                            {key.replace(/_/g, " ")}
                          </span>
                          <span className="text-gray-800 font-semibold">
                            {parseFloat(value).toFixed(2)} {CURRENCY_CODE}
                          </span>
                        </div>
                      ))}
                  </div>
                  <div className="mt-4 text-lg font-bold flex justify-between border-t pt-3">
                    <span className="text-purple-600">Total:</span>
                    <span className="text-gray-800">
                      {parseFloat(charges.total).toFixed(2)} {CURRENCY_CODE}
                    </span>
                  </div>
                </div>

                {/* Completion Proof Image */}
                {walk.completion_proof_url && (
                  <div className="bg-gray-50 p-3 sm:p-6 rounded-lg shadow-inner mt-4 sm:mt-6 flex flex-col items-center">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-700 mb-2 text-center">
                      Completion Proof (Walker Camera Pic)
                    </h2>
                    <img
                      src={walk.completion_proof_url}
                      alt="Completion Proof"
                      className="w-full max-w-xs sm:max-w-sm rounded-lg border object-contain"
                    />
                  </div>
                )}

                {/* Feedback Section */}
                {walk.feedback && (
                  <div className="bg-gray-50 p-3 sm:p-6 rounded-lg shadow-inner mt-4 sm:mt-6 flex flex-col gap-2 sm:gap-4">
                    <h2 className="text-base sm:text-lg font-semibold text-gray-700 flex items-center gap-2">
                      <FontAwesomeIcon icon={faStar} className="text-yellow-400" />
                      Feedback
                    </h2>
                    <div className="flex items-center gap-2">
                      <span className="text-sm sm:text-base font-medium text-gray-800">Rating:</span>
                      <StarRating rating={walk.feedback.rating} />
                      <span className="text-xs sm:text-sm text-gray-600">({walk.feedback.rating})</span>
                    </div>
                    <div className="text-sm sm:text-base text-gray-700">
                      {walk.feedback.feedback}
                    </div>
                  </div>
                )}

                {/* Close Button */}
                <div className="mt-8 text-center">
                  <button
                    onClick={onClose}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default WalkDetailModal;
